import { useCallback, useEffect, useRef } from 'react';
import { InternalError, reportError } from '@onramp/utils/errors';
import { useRouter } from 'next/router';
import { logMetric, MetricType } from '@cbhq/client-analytics';

const RISKIFIED_BEACON_ORIGIN = 'https://beacon.riskified.com';
const RISKIFIED_SCRIPT_ID = 'riskified-sdk';

type UseRiskifiedBeaconArgs = {
  enabled: boolean;
  sessionKey: string;
};

export function useRiskifiedBeacon({ enabled, sessionKey }: UseRiskifiedBeaconArgs) {
  const currentPathnameRef = useRef<string>();
  const isLoadedRef = useRef(false);
  const { asPath } = useRouter();
  const pathname = asPath.split(/[?#]/)[0];
  const latestPathnameRef = useRef(pathname);
  latestPathnameRef.current = pathname;

  const reportRoute = useCallback((path: string) => {
    if (!isLoadedRef.current || currentPathnameRef.current === path || !window.RISKX) return;
    currentPathnameRef.current = path;
    try {
      window.RISKX.go(path);
    } catch {
      reportError(new InternalError('Riskified beacon route update failed'), {
        severity: 'warning',
      });
      logMetric({
        metricName: 'web_riskified_route_failure',
        metricType: MetricType.count,
        value: 1,
        tags: { owner: 'payments-risk' },
      });
    }
  }, []);

  useEffect(() => {
    if (!enabled || !sessionKey) {
      currentPathnameRef.current = undefined;
      return undefined;
    }

    const url = new URL(RISKIFIED_BEACON_ORIGIN);
    url.searchParams.set('shop', window.location.hostname);
    url.searchParams.set('sid', sessionKey);

    const script = document.createElement('script');
    script.id = RISKIFIED_SCRIPT_ID;
    script.type = 'text/javascript';
    script.async = true;
    script.src = url.toString();

    const logRiskifiedMetric = (metricName: string) => {
      logMetric({
        metricName,
        metricType: MetricType.count,
        value: 1,
        tags: { owner: 'payments-risk' },
      });
    };

    const removeEventListeners = () => {
      script.removeEventListener('error', handleScriptError);
      script.removeEventListener('load', handleScriptLoad);
    };

    const handleScriptLoad = () => {
      logRiskifiedMetric('web_riskified_load_success');
      isLoadedRef.current = true;
      // Navigation may have completed while the async script was downloading.
      reportRoute(latestPathnameRef.current);
      removeEventListeners();
    };

    const handleScriptError = (event: Event) => {
      reportError(
        new InternalError('Riskified beacon script failed to load').addMetadata({
          source: 'useRiskifiedBeacon',
          scriptOrigin: RISKIFIED_BEACON_ORIGIN,
          eventType: event.type,
        }),
        { severity: 'warning' },
      );
      logRiskifiedMetric('web_riskified_load_failure');
      removeEventListeners();
    };

    script.addEventListener('error', handleScriptError);
    script.addEventListener('load', handleScriptLoad);

    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag?.parentNode) {
      firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
    } else {
      document.head.appendChild(script);
    }

    currentPathnameRef.current = window.location.pathname;

    return () => {
      isLoadedRef.current = false;
      removeEventListeners();
      // Stops explicit reports, not already-running vendor code.
      script.remove();
    };
  }, [enabled, sessionKey, reportRoute]);

  useEffect(() => {
    if (enabled && sessionKey) reportRoute(pathname);
  }, [enabled, sessionKey, pathname, reportRoute]);
}
